import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

import { FormLoginData } from 'dashboard/forms/FormLogin/FormLogin';
import useRedirectIfLoggedIn from 'dashboard/lib/hooks/useRedirectIfLoggedIn';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { createUserAuthentication } from 'dashboard/redux/thunk-actions/user-authentication';
import DashboardLoginView from 'dashboard/views/DashboardLoginView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { captureException } from 'lib/reporting';

const DashboardLoginRoute = (props: PropsFromRedux) => {
  useRedirectIfLoggedIn();

  const location = useLocation();
  const navigate = useNavigate();

  const { callback: login, error } = useApiCallback(
    async ({ emailAddress, password }: FormLoginData) => {
      props.clearMessages();

      await props.createUserAuthentication({
        emailAddress,
        password,
        location,
        navigate,
      });
    },
    [props.clearMessages, props.createUserAuthentication, location],
  );

  useApiErrorHandler({
    error,
    onNetworkError: () =>
      props.showMessage('Sorry, something went wrong. Please try again later or contact support.'),
    // This should be reworked to use error slugs once we support that.
    onError: (err) => {
      switch (err.status) {
        case 400:
          if (err.errors?.[0].title === 'Phone number unsubscribed') {
            return props.showMessage(
              'The provided number is opted out from receiving messages from us. Please try a different number.',
            );
          } else {
            return props.showMessage(
              'Sorry, something went wrong. Please try again later or contact support.',
            );
          }
        case 401:
          return props.showMessage('The password you provided is incorrect.');
        case 403:
          switch (err.errors?.[0].details) {
            case 'Organization requires SAML for authentication':
              return props.showMessage('This organization requires SAML for authentication.');
            case 'Organization is deactivated':
              return props.showMessage('This organization has been deactivated.');
            case 'User is locked due to too many failed authentication attempts':
              return props.showMessage(
                'Your account has been locked for 1 hour due to too many failed authentication attempts.',
              );
            case 'User is in a SAML org has no password':
              return props.showMessage(
                'User was created through SAML and has no password. Please login via SAML or reset your password.',
              );
            default:
              return props.showMessage(
                'Sorry, something went wrong. Please try again later or contact support.',
              );
          }
        case 404:
          return props.showMessage('An account with this email address could not be found.');
        case 406:
          return props.showMessage(
            'Sorry, something went wrong. Please try again later or contact support.',
          );
        case 429:
          if (err.errors?.[0].title === 'Rate limit exceeded') {
            return props.showMessage(
              'You are sending too many messages. Please try again in several minutes.',
            );
          } else {
            return props.showMessage(
              'Sorry, something went wrong. Please try again later or contact support.',
            );
          }
        default:
          captureException(err);
          return props.showMessage(
            'Sorry, something went wrong. Please try again later or contact support.',
          );
      }
    },
  });

  return <DashboardLoginView login={login} />;
};

const mapDispatchToProps = {
  clearMessages,
  showMessage,
  createUserAuthentication,
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardLoginRoute);
